import React, { useEffect, useMemo, useRef } from 'react';
import { Checkbox, Popover } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { BatchPushBoard, BatchPushColumn } from '../graphql/batchPush';
import {
    cellKey,
    columnKeys,
    formatNumber,
    groupAreas,
    rowsKeys,
    sumCounts,
    triState,
    TriState,
} from '../batchPushUtils';
import BatchColumnLabel from './BatchColumnLabel';

interface BatchPushMatrixProps {
    board: BatchPushBoard;
    counts: Map<string, number>;
    selected: Set<string>;
    /** 切换一组单元格：一个都没选时全部勾上，部分或全部选中时全部取消 */
    onToggle: (keys: string[]) => void;
    /** 全选框：未全选时全部勾上，已全选时全部取消 */
    onToggleAll: (keys: string[]) => void;
    folded: Set<string>;
    onToggleFold: (group: string) => void;
    /** 命中搜索的区域（行表头高亮，并滚动到第一个命中） */
    hitAreaIds: number[];
}

/**
 * 列宽（table-layout: fixed + colgroup）：区域、右侧两列固定宽度；批次列不定宽，平分剩余宽度（每列不小于
 * BATCH_COL_MIN_WIDTH，由表格最小宽度保证），批次少时右侧「已选/待推」「待DSP发布排线」仍贴在表格最右、区域列不被拉宽；
 * 批次多时表格按最小宽度横向滚动。没有批次列时才用一个无边框的占位列吸收剩余宽度，避免看起来像一个空批次列。
 */
const AREA_COL_WIDTH = 130;
const BATCH_COL_MIN_WIDTH = 190;
/**
 * 已选/待推列：按中文表头收窄到 110px（表体「1,234 / 5,678」量级仍可单行放下）；
 * 英文 / 西语文案在「/」两侧加空格，列窄时表头折成两行而不是顶出分隔线
 */
const SELECTED_COL_WIDTH = 110;
/** 待DSP发布排线列：14px 加粗的「待DSP发布排线 ?」约需 142px（含左右内边距），留少量余量取 148px，再窄会折行 */
const DSP_COL_WIDTH = 148;
/** 区域行相对首字母分组行的缩进，体现「A → A001」的层级 */
const AREA_ROW_INDENT = 24;
/** 表头、首字母分组行（A、B…）共用的灰底，与右侧两列表头同色 */
const GROUP_BG = 'bg-[#F1F2F5]';

/** 表头三态勾选框 */
const TriCheckbox: React.FC<{ state: TriState; disabled?: boolean; onChange: () => void; label: string }> = ({
    state,
    disabled,
    onChange,
    label,
}) => (
    <Checkbox
        checked={state === 2}
        indeterminate={state === 1}
        disabled={disabled}
        onChange={onChange}
        aria-label={label}
        onClick={(e) => e.stopPropagation()}
    />
);

/**
 * 批次推送矩阵：行 = 区域（按首字母分组，可收起），列 = 回仓重派 + 批次，格子 = 待推件数。
 * 表头、首列吸顶 / 吸左；全选、整列、整组、整行、单格均可勾选，表头展示全选 / 部分 / 未选三态。
 */
const BatchPushMatrix: React.FC<BatchPushMatrixProps> = ({
    board,
    counts,
    selected,
    onToggle,
    onToggleAll,
    folded,
    onToggleFold,
    hitAreaIds,
}) => {
    const { t } = useTranslation();
    const columns = useMemo(
        () => (board.batches ?? []).filter((c): c is BatchPushColumn & { wmsBatchNumber: string } => !!c.wmsBatchNumber),
        [board.batches],
    );
    const groups = useMemo(() => groupAreas((board.areas ?? []).filter((a) => a.areaId != null)), [board.areas]);
    const allKeys = useMemo(() => Array.from(counts.keys()), [counts]);
    const hitSet = useMemo(() => new Set(hitAreaIds), [hitAreaIds]);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (hitAreaIds.length === 0 || !containerRef.current) return;
        const row = containerRef.current.querySelector(`[data-area-id="${hitAreaIds[0]}"]`);
        if (row && typeof (row as HTMLElement).scrollIntoView === 'function') {
            (row as HTMLElement).scrollIntoView({ block: 'center', behavior: 'smooth' });
        }
    }, [hitAreaIds]);

    // 表头与首字母分组行（A、B…）同一灰底，整行一致；右侧两列表头同色
    const headerCell = `sticky top-0 z-10 ${GROUP_BG} border-b border-r border-gray-200 px-3 py-2 text-left font-medium align-top`;
    const stickyLeft = 'sticky left-0 z-[5] bg-white border-r border-gray-200';
    // 右侧两列列头（已选/待推、待DSP发布排线）：字体、字号同批次列头（14px），加粗
    const rightHeaderCell = 'z-20 !font-bold';
    // 右侧「已选/待推」「待DSP发布排线」两列吸右（PRD 悬停），right 偏移按固定列宽计算
    // 「已选/待推」列左侧投影：横向滚动时把吸右的两列与批次区分开（设计稿）
    const rightStyleSelected: React.CSSProperties = {
        position: 'sticky',
        right: DSP_COL_WIDTH,
        boxShadow: '-8px 0 10px -6px rgba(0, 0, 0, 0.15)',
    };
    const rightStyleDsp: React.CSSProperties = { position: 'sticky', right: 0 };
    // 「已选/待推」列左右两条竖线：左侧与批次区分隔，右侧与「待DSP发布排线」分隔；表头、分组行、区域行统一颜色
    const selectedColBorder = 'border-l border-r border-gray-200';
    const tableMinWidth = AREA_COL_WIDTH + columns.length * BATCH_COL_MIN_WIDTH + SELECTED_COL_WIDTH + DSP_COL_WIDTH;
    const hasBatchColumns = columns.length > 0;

    return (
        <div ref={containerRef} className="overflow-auto border border-gray-200 rounded" style={{ maxHeight: 'calc(100vh - 330px)' }}>
            {/* 列间分隔线：表头、分组行、区域行的批次列都带右边框（gray-200），与表头一致 */}
            <table className="border-separate text-sm" style={{ borderSpacing: 0, tableLayout: 'fixed', width: '100%', minWidth: tableMinWidth }}>
                <colgroup>
                    <col style={{ width: AREA_COL_WIDTH }} />
                    {/* 批次列不定宽：平分剩余宽度 */}
                    {columns.map((col) => (
                        <col key={col.wmsBatchNumber} />
                    ))}
                    {/* 无批次时的占位列：吸收剩余宽度 */}
                    {!hasBatchColumns && <col />}
                    <col style={{ width: SELECTED_COL_WIDTH }} />
                    <col style={{ width: DSP_COL_WIDTH }} />
                </colgroup>
                <thead>
                    <tr>
                        <th className={`${headerCell} left-0 z-20`}>
                            <div className="flex items-center gap-2">
                                <TriCheckbox
                                    state={triState(allKeys, selected)}
                                    disabled={allKeys.length === 0}
                                    onChange={() => onToggleAll(allKeys)}
                                    label={t('wms.batchPush.selectAll')}
                                />
                                <span>{t('wms.batchPush.columns.area')}</span>
                            </div>
                        </th>
                        {columns.map((col) => {
                            const keys = columnKeys(counts, col.wmsBatchNumber);
                            const selectedCount = sumCounts(keys.filter((k) => selected.has(k)), counts);
                            return (
                                <th
                                    key={col.wmsBatchNumber}
                                    className={`${headerCell} !px-4 ${keys.length ? 'cursor-pointer' : ''}`}
                                    onClick={() => keys.length && onToggle(keys)}
                                >
                                    {/* 批次列头左对齐；左右内边距 16px，与相邻列拉开间距 */}
                                    <div className="flex items-start justify-start gap-2">
                                        <TriCheckbox
                                            state={triState(keys, selected)}
                                            disabled={keys.length === 0}
                                            onChange={() => onToggle(keys)}
                                            label={col.wmsBatchNumber}
                                        />
                                        <div className="min-w-0 text-left">
                                            <BatchColumnLabel column={col} showDefaultTip />
                                            {/* 已选 / 待推：未选（已选为 0）时白底灰字（表头已是灰底，用白底区分），有选中时紫底、已选数加粗高亮 */}
                                            <span
                                                className={`inline-block mt-1 px-2 rounded-full text-xs ${
                                                    selectedCount > 0 ? 'bg-indigo-50' : 'bg-white text-gray-500'
                                                }`}
                                                data-testid={`column-selected-pending-${col.wmsBatchNumber}`}
                                            >
                                                {selectedCount > 0
                                                    ? <b className="text-indigo-600">{formatNumber(selectedCount)}</b>
                                                    : formatNumber(selectedCount)} / {formatNumber(col.pendingCount)}
                                            </span>
                                        </div>
                                    </div>
                                </th>
                            );
                        })}
                        {!hasBatchColumns && <th className={`sticky top-0 z-10 ${GROUP_BG} border-b border-gray-200`} aria-hidden />}
                        {/* 右侧两列表头：灰底、加粗（设计稿） */}
                        <th className={`${headerCell} ${rightHeaderCell} ${selectedColBorder}`} style={rightStyleSelected}>{t('wms.batchPush.columns.selectedPending')}</th>
                        <th className={`${headerCell} ${rightHeaderCell}`} style={rightStyleDsp}>
                            <span>{t('wms.batchPush.columns.dspPendingPublish')}</span>
                            {/* PRD：点击问号有解释说明 */}
                            <Popover trigger="click" content={<div className="max-w-xs">{t('wms.batchPush.columns.dspPendingPublishTip')}</div>}>
                                <QuestionCircleOutlined className="ml-1 text-gray-400 cursor-pointer" data-testid="dsp-pending-tip" />
                            </Popover>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {groups.map(({ group, areas }) => {
                        const groupAreaIds = areas.map((a) => Number(a.areaId));
                        const groupKeys = rowsKeys(counts, groupAreaIds);
                        const isFolded = folded.has(group);
                        return (
                            <React.Fragment key={group}>
                                {/* 首字母分组行整行灰底（区域、批次列、右侧两列同色） */}
                                <tr className={GROUP_BG}>
                                    <th
                                        className={`${stickyLeft} !bg-[#F1F2F5] px-3 py-1 text-left border-b ${groupKeys.length ? 'cursor-pointer' : ''}`}
                                        onClick={() => groupKeys.length && onToggle(groupKeys)}
                                    >
                                        <div className="flex items-center gap-2">
                                            <TriCheckbox
                                                state={triState(groupKeys, selected)}
                                                disabled={groupKeys.length === 0}
                                                onChange={() => onToggle(groupKeys)}
                                                label={group}
                                            />
                                            <button
                                                type="button"
                                                className="font-medium"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    onToggleFold(group);
                                                }}
                                            >
                                                {isFolded ? '▸' : '▾'} {group}
                                            </button>
                                        </div>
                                    </th>
                                    {/* 批次列逐列渲染，保留列间分隔线（无批次时为一个无分隔的占位格） */}
                                    {hasBatchColumns
                                        ? columns.map((col) => <td key={col.wmsBatchNumber} className={`border-b border-r border-gray-200 ${GROUP_BG}`} />)
                                        : <td className={`border-b ${GROUP_BG}`} />}
                                    <td className={`border-b ${GROUP_BG} ${selectedColBorder}`} style={rightStyleSelected} />
                                    <td className={`border-b ${GROUP_BG}`} style={rightStyleDsp} />
                                </tr>
                                {!isFolded && areas.map((area) => {
                                    const areaId = Number(area.areaId);
                                    const rowKeys = rowsKeys(counts, [areaId]);
                                    const rowSelected = sumCounts(rowKeys.filter((k) => selected.has(k)), counts);
                                    const hit = hitSet.has(areaId);
                                    return (
                                        <tr key={areaId} data-area-id={areaId}>
                                            <th
                                                className={`${stickyLeft} px-3 py-2 text-left font-normal border-b ${hit ? 'bg-yellow-50' : ''} ${rowKeys.length ? 'cursor-pointer' : ''}`}
                                                onClick={() => rowKeys.length && onToggle(rowKeys)}
                                            >
                                                {/* 区域行相对首字母分组行缩进，体现层级 */}
                                                <div className="flex items-center gap-2" style={{ paddingLeft: AREA_ROW_INDENT }}>
                                                    <TriCheckbox
                                                        state={triState(rowKeys, selected)}
                                                        disabled={rowKeys.length === 0}
                                                        onChange={() => onToggle(rowKeys)}
                                                        label={area.areaName ?? String(areaId)}
                                                    />
                                                    <span className={hit ? 'font-bold' : ''}>{area.areaName ?? areaId}</span>
                                                </div>
                                            </th>
                                            {columns.map((col) => {
                                                const key = cellKey(col.wmsBatchNumber, areaId);
                                                const count = counts.get(key);
                                                const on = selected.has(key);
                                                return (
                                                    <td
                                                        key={key}
                                                        // 未选白底、选中浅紫，不加悬停底色：取消勾选后即使鼠标还停在格子上也立即恢复白底
                                                        className={`border-b border-r border-gray-200 px-4 py-2 text-center ${
                                                            count ? 'cursor-pointer' : 'text-gray-300'
                                                        } ${on ? 'bg-indigo-50 font-bold text-indigo-600' : ''}`}
                                                        onClick={() => count && onToggle([key])}
                                                        aria-pressed={on}
                                                    >
                                                        {count ? formatNumber(count) : '-'}
                                                    </td>
                                                );
                                            })}
                                            {!hasBatchColumns && <td className="border-b border-gray-100" />}
                                            <td
                                                className={`border-b px-3 py-2 text-right whitespace-nowrap bg-[#F7F8FA] z-[4] ${selectedColBorder}`}
                                                style={rightStyleSelected}
                                                data-testid={`area-selected-pending-${areaId}`}
                                            >
                                                {/* 区域无待推批次数据：「- / -」；有数据：「已选 / 待推」，未勾选为「0 / 待推」 */}
                                                {(area.pendingCount ?? 0) > 0 ? (
                                                    <>
                                                        <b className={rowSelected ? 'text-indigo-600' : 'text-gray-400'}>{formatNumber(rowSelected)}</b>
                                                        <span className="text-gray-400"> / </span>
                                                        {formatNumber(area.pendingCount)}
                                                    </>
                                                ) : (
                                                    <span className="text-gray-400">- / -</span>
                                                )}
                                            </td>
                                            {/* 待DSP发布排线：整列浅灰底，数字加粗（设计稿）；无数据的「-」保持浅色常规字重 */}
                                            <td
                                                className="border-b px-3 py-2 text-right bg-[#F7F8FA] z-[4]"
                                                style={rightStyleDsp}
                                                data-testid={`area-dsp-pending-${areaId}`}
                                            >
                                                {area.dspPendingPublishCount
                                                    ? <b className="text-gray-700">{formatNumber(area.dspPendingPublishCount)}</b>
                                                    : <span className="text-gray-300">-</span>}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </React.Fragment>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

export default BatchPushMatrix;
